import { useCallback, useEffect, useMemo, useState } from "react";

import { GetOrganizations } from "@/services/fhir";

import {
  isStoreApiReady,
  getMarketplaceRegistryConfigError,
  resolveStoreConfigFromOrganization,
} from "../constants/organizationStoreRegistry";
import { PHARMA_ORGANIZATION_TYPE_CODE } from "../../services/pharmaConsentService";
import type { WooStoreConfig } from "../types";

function sortDispensaries(stores: WooStoreConfig[]): WooStoreConfig[] {
  return [...stores].sort((a, b) => {
    const aActive = isStoreApiReady(a) ? 1 : 0;
    const bActive = isStoreApiReady(b) ? 1 : 0;
    if (aActive !== bActive) return bActive - aActive;
    return a.name.localeCompare(b.name, "pt-BR");
  });
}

/** Lista dispensações (Organizations ecommerce) + credenciais do registry. */
export function useWooCommerceStores(): {
  stores: WooStoreConfig[];
  activeStores: WooStoreConfig[];
  isConfigured: boolean;
  loading: boolean;
  error: string | null;
  configError: string | null;
  reload: () => void;
} {
  const [stores, setStores] = useState<WooStoreConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const configError = getMarketplaceRegistryConfigError();

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const response = (await GetOrganizations({
          type: PHARMA_ORGANIZATION_TYPE_CODE,
          active: true,
          count: 200,
        })) as fhir5.Bundle;

        const entries = response?.entry ?? [];
        const mapped: WooStoreConfig[] = [];

        for (const entry of entries) {
          const resource = entry.resource;
          if (!resource || resource.resourceType !== "Organization") continue;
          const config = resolveStoreConfigFromOrganization(resource);
          if (config) mapped.push(config);
        }

        if (!cancelled) {
          setStores(sortDispensaries(mapped));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Não foi possível carregar os dispensadores.",
          );
          setStores([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const activeStores = useMemo(
    () => stores.filter(isStoreApiReady),
    [stores],
  );

  return {
    stores,
    activeStores,
    isConfigured: activeStores.length > 0,
    loading,
    error,
    configError,
    reload,
  };
}
